import React from 'react';
import './Banner.css';

// info = bloc d'information (gris, icone "i"). success/danger/warning = bannieres d'action et
// d'etat (vert/rouge/orange), sans icone : elles signalent un etat ou appellent une action.
export type BannerVariant = 'info' | 'success' | 'danger' | 'warning';

interface BannerProps {
  // Couleur/semantique. Defaut 'info'.
  variant?: BannerVariant;
  // false (defaut) = contour (bordure fine + texte colore) ; true = fond plein.
  filled?: boolean;
  // Message personnalisable (texte ou JSX).
  message: React.ReactNode;
  // Masquer l'icone (ex. bande "derniere mise a jour").
  hideIcon?: boolean;
  // Classe additionnelle pour ajustements ponctuels (marges...).
  className?: string;
  // Titre optionnel (gras, au-dessus du message). Active la disposition "riche".
  title?: React.ReactNode;
  // Action optionnelle (ex. bouton), rendue SOUS le message (jamais a droite). Active la disposition "riche".
  action?: React.ReactNode;
}

// Icone info uniquement : cercle gris #595959 en contour (sans fond) + "i" gris.
// Pas d'icone pour les variants success / danger.
const InfoIcon: React.FC = () => (
  <svg
    className="ui-banner__icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#595959"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="8" x2="12" y2="8" />
    <line x1="12" y1="11" x2="12" y2="16" />
  </svg>
);

/**
 * Banniere d'information reutilisable (inline, persistante).
 * - variant : info (icone grise) | success (vert) | danger (rouge)
 * - filled  : contour (defaut) ou fond plein
 * - message : texte personnalisable
 *
 * Ex. : <Banner variant="info" message="..." />
 *       <Banner variant="success" filled hideIcon message="Derniere mise a jour..." />
 */
const Banner: React.FC<BannerProps> = ({
  variant = 'info',
  filled = false,
  message,
  hideIcon = false,
  className,
  title,
  action,
}) => {
  const riche = title != null || action != null;
  const classes = [
    'ui-banner',
    `ui-banner--${variant}`,
    filled ? 'ui-banner--filled' : '',
    riche ? 'ui-banner--rich' : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  // Disposition simple (defaut). `div` et non `p` : cf. la note de la disposition riche plus bas —
  // les DEUX branches enveloppent `message`, et un appelant sans `title` passe par ici.
  if (!riche) {
    return (
      <div className={classes} role="note">
        {variant === 'info' && !hideIcon && <InfoIcon />}
        <div className="ui-banner__text">{message}</div>
      </div>
    );
  }

  // Disposition riche : titre + message empiles, action rendue EN DESSOUS (jamais a droite).
  return (
    <div className={classes} role="note">
      {variant === 'info' && !hideIcon && <InfoIcon />}
      <div className="ui-banner__content">
        {title != null && <span className="ui-banner__title">{title}</span>}
        {/* `div` et non `p` : `message` est un ReactNode, donc parfois du JSX BLOC (cf. ModalArticles,
            qui y passe <GrillePrixInfoAcheteur />). Un <p> ne peut contenir ni <p> ni <div> — le
            navigateur ferme le premier d'autorite et React le signale. Le rendu ne change pas :
            `.ui-banner__text` porte ses marges explicitement, et les deux seuls selecteurs qui la
            visent (ici + `.opms__ia-banner`) ciblent la CLASSE, jamais l'element. */}
        <div className="ui-banner__text">{message}</div>
      </div>
      {action != null && <div className="ui-banner__action">{action}</div>}
    </div>
  );
};

export default Banner;
